import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useClientData } from '../../../../providers/ClientData.provider';
import usePostPaylinkCancel from '../../../../hooks/useApi/usePostPaylinkCancel';
import useFetchELinxHistory from '@/hooks/useApi/useFetchELinxHistory';
import AccountDetails from './AccountDetails';
import AccountContext, { AccountCtx } from '../Dialogue/AccountContext';
import AccountDialogue from '../Dialogue/AccountDialogue';
import { AccountDialogueHandler, DialogueHandlerProps } from '../Dialogue/types';
import useFileDownload from '@/hooks/useFiledDownload';
import styles from './ElinxDetails.module.scss';
import { useTranslation } from 'react-i18next';
import { UtilButton } from 'UI';

const { Box, Title, Button, DropDown } = vp;

type FC = FunctionComponent;

const ElinxDetailsContainer: FC = () => {
  const history = useHistory();
  const { ELinxId } = useParams<{ ELinxId: string }>();
  //const { state: locationState } = useLocation<DataProps>();
  const { response } = useClientData();
  const { printFile, downloadPdfPage } = useFileDownload();
  const [paylinkCancelResponse, postPaylinkCancel] = usePostPaylinkCancel({});
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [shouldCancelELinx, setShouldCancelELinx] = useState(false);
  const [showDeleteAccountConfirmAlert, setShowDeleteAccountConfirmAlert] = useState(false);
  const [showDeleteAccountErrorAlert, setShowDeleteAccountErrorAlert] = useState(false);
  const [showDeleteAccountSuccessAlert, setShowDeleteAccountSuccessAlert] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');
  const [eLinx, setELinx] = useState<vp.ELinxHistoryItem>();
  const { t } = useTranslation('translations', { keyPrefix: 'ElinxHistory' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const [, , , fetchElinxHistory] = useFetchELinxHistory({});

  const DeleteAccount: AccountCtx = {
    showDeleteAccountConfirmAlert,
    showDeleteAccountErrorAlert,
    showDeleteAccountSuccessAlert,
    deleteAccountError,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await fetchElinxHistory({ ELinxRequestID: ELinxId });
        const findElinx = Object.values(resp.ELinxRequests || {}).pop();
        if (findElinx) {
          setELinx(findElinx);
        }
      } catch (error) {
        console.error('Error fetching ELinx history:', error);
      }
    };

    fetchData();
  }, [ELinxId]);

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountDialogueHandler = {
        delete: {
          confirm: {
            default: () => {
              setShowDeleteAccountSuccessAlert(false);
              history.goBack();
            },
          },
          fail: {
            default: () => {
              setShowDeleteAccountErrorAlert(false);
            },
          },
        },
        confirm: {
          confirm: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
              setShouldCancelELinx(true);
            },
          },
          fail: {
            default: () => {
              setShowDeleteAccountConfirmAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (shouldCancelELinx && eLinx) {
      postPaylinkCancel({
        PortalUser: response.LoginUserName,
        ELinxRequestID: eLinx.ELinxRequestID.toString(),
      });
    }
  }, [shouldCancelELinx]);

  useEffect(() => {
    if (shouldCancelELinx) {
      setShouldCancelELinx(false);
      const { Success, ErrorMessage } = paylinkCancelResponse;
      if (Success === true) {
        setShowDeleteAccountSuccessAlert(true);
      } else {
        setShowDeleteAccountErrorAlert(true);
        setDeleteAccountError(ErrorMessage);
      }
    }
  }, [paylinkCancelResponse]);
  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfPage('elinx-overview', 'elinx-details');
    }
    if (isPrinting) {
      printFile('elinx-overview');
    }
    setIsExportingPDF(false);
    setIsPrinting(false);
  }, [isExportingPDF, isPrinting]);

  return (
    <AccountContext.Provider value={DeleteAccount}>
      {eLinx && (
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/elinx-history');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('title')}
          </UtilButton>
          <Box width="100%" flx="row" jc="sb" mb="md">
            <Box mb="md">
              <Title
                title={
                  eLinx.PaymentType === 'fund'
                    ? 'Collection Overview'
                    : eLinx.PaymentType === 'withdraw'
                      ? 'Payment Overview'
                      : 'Connection Requsition Overview'
                }
              />
            </Box>
            <Box flx="row" gap="sm" ai="center">
              {eLinx.Status === 'pending' && (
                <Button
                  id="delete-elinx"
                  label="Delete Transaction"
                  color="secondary"
                  width="160px"
                  height="38px"
                  onClick={() => {
                    setShowDeleteAccountConfirmAlert(true);
                  }}
                />
              )}
              <div className={styles.dropdownContainer}>
                <Button
                  id="export-transactions"
                  color="none"
                  label="Export"
                  height="42px"
                  textSize={1}
                  background="transparent"
                  noShadow
                  icon="arrowDown"
                  iconLocation="right"
                  onClick={() => {
                    setIsMenuVisible(true);
                  }}
                />
                {isMenuVisible && (
                  <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                    <Button
                      id="export-PDF-btn"
                      label="PDF"
                      background="transparent"
                      textSize={1}
                      width="100%"
                      noShadow
                      onClick={() => {
                        setIsExportingPDF(true);
                      }}
                    />
                    <Button
                      id="export-Print-btn"
                      label="Print"
                      background="transparent"
                      textSize={1}
                      width="100%"
                      noShadow
                      onClick={() => {
                        setIsPrinting(true);
                      }}
                    />
                  </DropDown>
                )}
              </div>
            </Box>
          </Box>
          <div id="elinx-overview">
            <AccountDetails eLinx={eLinx} />
          </div>
        </Box>
      )}
      <AccountDialogue handleAccountDialogue={handlers.handleAccountDialogue} />
    </AccountContext.Provider>
  );
};

export default ElinxDetailsContainer;
